/**
 * StatusUpdateFeed — chronological list of Oppdateringer / status updates for one subject.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  UserCircleIcon,
  UsersIcon,
  LockClosedIcon,
  EyeSlashIcon,
  TrashIcon,
  PencilSquareIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import {
  CaseItemDescription,
  MentionTextEditor,
  extractMentionedUsers,
  extractModuleTags,
} from "../../utils/MentionTextEditor";
import { DocumentManagerV6 } from "../Media/DocumentManagerV6";
import { dateToString } from "../../Utils";
import { TWclassNames } from "../../utils/Div";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { deleteStatusUpdate, updateStatusUpdate } from "../../../api/statusUpdates";
import { useDocumentUploadQueueOptional } from "../../../contexts/document-upload-queue-context";

const VISIBILITY_OPTIONS = [
  { value: "team", labelKey: "Alle med tilgang", Icon: UsersIcon },
  { value: "admin", labelKey: "Kun admin", Icon: LockClosedIcon },
  { value: "self", labelKey: "Kun meg", Icon: EyeSlashIcon },
];

function tagsFromBody(html) {
  const mentionedUsers = extractMentionedUsers(html || "");
  const slashTags = extractModuleTags(html || "");
  const userTags = mentionedUsers.map((u) => ({
    module: "user",
    module_param: String(u.id),
  }));
  const seen = new Set();
  const tags = [];
  [...slashTags, ...userTags].forEach((tg) => {
    const module = String(tg.module || "");
    const module_param =
      tg.module_param != null ? String(tg.module_param) : "";
    const key = `${module}:${module_param}`;
    if (!module || !module_param || seen.has(key)) return;
    seen.add(key);
    tags.push({ module, module_param });
  });
  return {
    tags,
    notifyEmployees: mentionedUsers.map((u) => ({ id: u.id })),
  };
}

function resolveSubjectKey(post) {
  if (post?.subject_key) return String(post.subject_key).toLowerCase();
  const type = String(post?.subject_type || "").toLowerCase();
  if (type === "project") return "project";
  if (type === "customer") return "customer";
  if (type === "workorder" || type === "work_order") return "work_order";
  if (type === "projectactivity" || type === "project_activity") {
    return "project_activity";
  }
  if (type === "deviation") return "deviation";
  if (type === "jobopening" || type === "job_opening") return "job_opening";
  if (type === "jobapplication" || type === "job_application") {
    return "job_application";
  }
  return null;
}

function subjectHref(post) {
  const key = resolveSubjectKey(post);
  const id = post?.subject_id;
  if (!key || !id) return null;
  if (key === "project") return `/dashboard/projects/${id}`;
  if (key === "customer") return `/dashboard/customers/${id}`;
  if (key === "work_order") return `/dashboard/work_orders/${id}`;
  if (key === "deviation") return `/dashboard/hse-ks/deviations/${id}`;
  if (key === "job_opening") return `/dashboard/hr/job-openings/${id}`;
  if (key === "job_application") {
    const openingId = post.job_opening_id;
    return openingId
      ? `/dashboard/hr/job-openings/${openingId}/candidates/${id}`
      : null;
  }
  if (key === "project_activity") {
    const projectId = post.project_id || post.subject?.project_id;
    return projectId ? `/dashboard/projects/${projectId}` : null;
  }
  return null;
}

export function VisibilityBadge({ visibility }) {
  const { t } = useTranslation();
  if (visibility === "admin") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
        <LockClosedIcon className="h-3 w-3" />
        {t("Kun admin")}
      </span>
    );
  }
  if (visibility === "self") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700">
        <EyeSlashIcon className="h-3 w-3" />
        {t("Kun meg")}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">
      <UsersIcon className="h-3 w-3" />
      {t("Alle med tilgang")}
    </span>
  );
}

function StatusUpdateCard({
  post,
  customer,
  assumeOwn = false,
  onDeleted,
  onUpdated,
}) {
  const { t } = useTranslation();
  const currentUserId = useSelector((state) => state.session.currentUser?.id);
  const author = post.user || {};
  const isOwn =
    assumeOwn ||
    (author.id != null &&
      currentUserId != null &&
      Number(author.id) === Number(currentUserId));
  const href = subjectHref(post);
  const hasDocs = Array.isArray(post.documents) && post.documents.length > 0;
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(post.title || "");
  const [body, setBody] = useState(post.body_html || "");
  const [visibility, setVisibility] = useState(post.visibility || "team");
  const [saving, setSaving] = useState(false);
  const queue = useDocumentUploadQueueOptional();
  const pendingUploads = queue?.sessions?.filter((s) => s.module === "status_update" && String(s.moduleParam) === String(post.id) && s.status !== "completed") || [];
  const [uploadedDocs, setUploadedDocs] = useState(0);
  useEffect(() => {
    const uploaded = (event) => {
      if (event.detail?.module === "status_update" && String(event.detail?.moduleParam) === String(post.id)) setUploadedDocs((version) => version + 1);
    };
    window.addEventListener("promang:documents-uploaded", uploaded);
    return () => window.removeEventListener("promang:documents-uploaded", uploaded);
  }, [post.id]);

  const startEdit = () => {
    setTitle(post.title || "");
    setBody(post.body_html || "");
    setVisibility(post.visibility || "team");
    setEditing(true);
  };

  const handleDelete = async () => {
    if (!post?.id) return;
    if (typeof window !== "undefined" && !window.confirm(t("Slette denne oppdateringen?"))) {
      return;
    }
    try {
      const res = await deleteStatusUpdate(post.id);
      if (!res?.__error && (res?.success === true || res == null || res === "")) {
        onDeleted?.(post.id);
        return;
      }
      toast.error(
        res?.errors?.[0] ||
          res?.error ||
          res?.message ||
          t("status_update.delete_failed")
      );
    } catch {
      toast.error(t("status_update.delete_failed"));
    }
  };

  const handleSave = async () => {
    if (!post?.id || saving) return;
    const bodyHtml = body?.trim() || "";
    const { tags, notifyEmployees } = tagsFromBody(bodyHtml);
    setSaving(true);
    try {
      const res = await updateStatusUpdate(post.id, {
        title: title?.trim() || "",
        body_html: bodyHtml || " ",
        visibility,
        tags,
        notifyEmployees: notifyEmployees.length ? notifyEmployees : undefined,
      });
      if (res?.__error || !res?.id) {
        toast.error(
          res?.errors?.[0] ||
            res?.error ||
            res?.message ||
            t("status_update.save_failed")
        );
        return;
      }
      toast.success(t("status_update.saved"));
      setEditing(false);
      onUpdated?.(res);
    } catch {
      toast.error(t("status_update.save_failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,.04)]">
      <header className="mb-2 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <UserCircleIcon className="h-8 w-8 flex-shrink-0 text-slate-300" />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-slate-900">
              {author.displayTitle || author.name || t("Bruker")}
            </p>
            <p className="text-xs text-slate-400">
              {post.created_at ? dateToString(post.created_at) : ""}
              {post.updated_at && post.updated_at !== post.created_at
                ? ` · ${t("status_update.edited")}`
                : ""}
            </p>
            {post.subject_label && href ? (
              <Link
                to={href}
                className="mt-0.5 block truncate text-xs font-medium text-[#0B3A78] hover:underline"
              >
                {post.subject_label}
              </Link>
            ) : !post.subject_id ? (
              <span className="mt-0.5 inline-flex rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500">
                {t("status_update.mine_company_scope")}
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {post.appeared_via === "mention" ? (
            <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
              {t("status_update.mentioned_here")}
            </span>
          ) : null}
          <VisibilityBadge visibility={editing ? visibility : post.visibility} />
          {isOwn && !editing ? (
            <>
              <button
                type="button"
                onClick={startEdit}
                className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                title={t("status_update.edit")}
              >
                <PencilSquareIcon className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600"
                title={t("Slett")}
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </>
          ) : null}
        </div>
      </header>

      {editing ? (
        <div className="flex flex-col gap-3">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("Tittel (valgfritt)")}
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-900 placeholder-slate-400 focus:border-[#1463C7] focus:outline-none focus:ring-1 focus:ring-[#1463C7]"
          />
          <MentionTextEditor
            variant="innlegg"
            value={body}
            onChange={setBody}
            placeholder={t(
              "Skriv en oppdatering… bruk @ for å nevne, / for å koble modul"
            )}
            minHeight={96}
            maxHeight={220}
            disabled={saving}
            enableSlashCommands={true}
            customer={customer}
          />
          <div className="inline-flex overflow-hidden rounded-full border border-slate-200 bg-slate-50 self-start">
            {VISIBILITY_OPTIONS.map(({ value, labelKey, Icon }) => (
              <button
                key={value}
                type="button"
                onClick={() => setVisibility(value)}
                className={TWclassNames(
                  "flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium transition-colors",
                  visibility === value
                    ? "bg-[#1463C7] text-white"
                    : "text-slate-600 hover:bg-slate-100"
                )}
                title={t(labelKey)}
              >
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{t(labelKey)}</span>
              </button>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={saving}
              className="rounded-xl px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
            >
              {t("status_update.cancel")}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded-xl bg-[linear-gradient(135deg,#1463C7,#2D8BE8)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              {saving ? t("Sender…") : t("status_update.save")}
            </button>
          </div>
        </div>
      ) : (
        <>
          {post.title ? (
            <h4 className="mb-1 text-[15px] font-semibold text-slate-900">
              {post.title}
            </h4>
          ) : null}

          <div
            className={TWclassNames(
              "text-sm text-slate-700",
              post.source === "transcript" && "italic"
            )}
          >
            <CaseItemDescription value={post.body_html} />
          </div>

          {pendingUploads.length > 0 && <div className="mt-3 flex flex-wrap gap-2">{pendingUploads.flatMap((session) => (session.previewItems || (session.previews || []).map((url) => ({ url, type: "image/" }))).map((entry) => entry.type?.startsWith("image/") ? <img key={entry.url} src={entry.url} alt={entry.title || t("statusAttachments.backgroundHint")} className="h-20 w-20 rounded object-cover" /> : <div key={entry.url} className="flex h-20 w-28 flex-col items-center justify-center gap-1 rounded bg-slate-100 p-2 text-slate-600"><DocumentTextIcon className="h-6 w-6 shrink-0" aria-hidden="true" /><span className="line-clamp-2 break-all text-xs">{entry.title}</span></div>))}</div>}
          {hasDocs || uploadedDocs ? (
            <div className="mt-3 border-t border-slate-100 pt-3">
              <DocumentManagerV6
                key={`su-docs-${post.id}-${uploadedDocs}`}
                module="status_update"
                moduleParam={post.id}
                mode="grid"
                showUpload={false}
                showExisting={true}
                allowDelete={isOwn}
                allowCopy={false}
                allowMove={false}
                allowEdit={isOwn}
                autoLoad={true}
                maxDisplay={8}
              />
            </div>
          ) : null}
        </>
      )}
    </article>
  );
}

export function StatusUpdateFeed({
  posts = [],
  loading = false,
  customer,
  assumeOwn = false,
  emptyMessage,
  onDeleted,
  onUpdated,
}) {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="py-8">
        <ResourceLoading />
      </div>
    );
  }

  if (!posts.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
        {emptyMessage || t("Ingen oppdateringer ennå. Skriv den første!")}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {posts.map((post) => (
        <StatusUpdateCard
          key={post.id}
          post={post}
          customer={customer}
          assumeOwn={assumeOwn}
          onDeleted={onDeleted}
          onUpdated={onUpdated}
        />
      ))}
    </div>
  );
}

export default StatusUpdateFeed;
